/**
 * WorkdayCards.js
 *
 * Inline Munkanap cards:
 * - {@link WorkdayHeroCard}  hero card at the top of the "Mai napom" briefing (not started -> big orange CTA; running ->
 *   live elapsed + ring + "Lezárás"),
 * - {@link WorkdayStrip}     slim status strip at the top of the Time Tracker screen (status + start / stop).
 * Both render nothing when the feature is unsupported / not loaded.
 *
 * @module components/workday/WorkdayCards
 */

import React from 'react';
import {Pressable, StyleSheet, Text, View} from 'react-native';
import {LinearGradient} from 'expo-linear-gradient';
import LottieView from 'lottie-react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import WorkdayRing from './WorkdayRing';
import {PrimaryButton, WorkModeSelector, WorkdayControls} from './WorkdayParts';
import useWorkdayStopFlow from './useWorkdayStopFlow';
import {BRAND, useManagementTheme, withAlpha} from '../../helpers/managementTheme';
import {useWorkday, useWorkdayLive} from '../../helpers/WorkdayContext';
import {formatHM, formatHMS, isReminderDue} from '../../helpers/workdayRules';

const fmtClock = (iso) => (iso ? new Date(iso).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '--:--');

/**
 * Workday hero card of the briefing.
 * @param {{onAfterStart?: () => void}} props `onAfterStart` runs after a successful start.
 * @returns {JSX.Element|null} card.
 */
export function WorkdayHeroCard({onAfterStart}) {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {supported, loaded, state, busy, start} = useWorkday();
    const live = useWorkdayLive(1000);
    if (!supported || !loaded || !state) {
        return null;
    }

    if (!live.running) {
        return (
            <View style={[styles.hero, {backgroundColor: m.colors.surface, borderColor: withAlpha(BRAND.base, 0.35)}, m.shadow(2)]}>
                <View style={styles.heroTop}>
                    <View style={[styles.lottieWrap, {backgroundColor: withAlpha(BRAND.base, m.isDark ? 0.2 : 0.1)}]}>
                        <LottieView source={require('../../../assets/animations/workday-clock.json')} autoPlay loop style={styles.lottie} />
                    </View>
                    <View style={styles.heroText}>
                        <Text style={[styles.kicker, {color: BRAND.base}]} numberOfLines={1}>{t('workday_kicker').toUpperCase()}</Text>
                        <Text style={[styles.heroTitle, {color: m.colors.heading}]} numberOfLines={2}>{t('workday_hero_idle_title')}</Text>
                        <Text style={[styles.heroSub, {color: m.colors.subText}]} numberOfLines={2}>{t('workday_hero_idle_text')}</Text>
                    </View>
                </View>
                <WorkModeSelector />
                <PrimaryButton
                    label={t('workday_start')}
                    icon="play-circle-outline"
                    busy={busy}
                    onPress={async () => {
                        const next = await start({});

                        if (next && onAfterStart) {
                            onAfterStart();
                        }
                    }}
                />
            </View>
        );
    }

    const paused = live.paused;
    const due = !paused && isReminderDue(state, Date.now());
    const toneColor = paused || live.tone === 'warn' ? '#F59E0B' : (live.tone === 'danger' ? '#DC2626' : BRAND.base);

    return (
        <View style={[styles.hero, {backgroundColor: m.colors.surface, borderColor: withAlpha(toneColor, 0.4)}, m.shadow(2)]}>
            <View style={styles.heroTop}>
                <WorkdayRing size={88} stroke={8} progress={live.progress} tone={live.tone} trackColor={withAlpha(m.colors.subText, 0.2)}>
                    <Text style={[styles.ringTime, {color: m.colors.heading}]} numberOfLines={1} adjustsFontSizeToFit minimumFontScale={0.7}>{formatHM(live.workedSec)}</Text>
                </WorkdayRing>
                <View style={styles.heroText}>
                    <Text style={[styles.kicker, {color: toneColor}]} numberOfLines={1}>
                        {(paused ? t('workday_status_paused') : (due ? t('workday_reminder_title') : t('workday_hero_running_title'))).toUpperCase()}
                    </Text>
                    <Text style={[styles.heroClock, {color: m.colors.heading}]} numberOfLines={1}>{formatHMS(live.workedSec)}</Text>
                    <Text style={[styles.heroSub, {color: m.colors.subText}]} numberOfLines={2}>
                        {`${t('workday_since', {time: fmtClock(state.running.startedAt)})} · ${t('workday_target', {time: formatHM(live.targetSec)})}${live.breakSec > 0 ? ` · ${t('workday_break_time')} ${formatHM(live.breakSec)}` : ''}`}
                    </Text>
                </View>
            </View>
            <WorkModeSelector />
            <WorkdayControls />
        </View>
    );
}

/**
 * Time Tracker strip: status + start / stop.
 * @returns {JSX.Element|null} strip.
 */
export function WorkdayStrip() {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const {supported, loaded, state, busy, start, openSheet, pause, resume} = useWorkday();
    const live = useWorkdayLive(1000);
    const {requestStop} = useWorkdayStopFlow();

    if (!supported || !loaded || !state) {
        return null;
    }

    const running = live.running;
    const paused = running && live.paused;

    return (
        <LinearGradient
            colors={running ? ['#1f2a44', '#243656'] : [m.colors.surface, m.colors.surface]}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={[styles.strip, {borderColor: running ? 'transparent' : m.colors.border}, m.shadow(1)]}
        >
            <Pressable onPress={openSheet} accessibilityRole="button" style={styles.stripMain}>
                <WorkdayRing size={44} stroke={5} progress={running ? live.progress : 0} tone={live.tone} trackColor={running ? 'rgba(255,255,255,0.22)' : withAlpha(m.colors.subText, 0.2)}>
                    <Icon name={running ? 'briefcase-clock-outline' : 'briefcase-outline'} size={17} color={running ? '#FFFFFF' : BRAND.base} />
                </WorkdayRing>
                <View style={styles.heroText}>
                    <Text style={[styles.stripTitle, {color: running ? '#FFFFFF' : m.colors.heading}]} numberOfLines={1}>
                        {running ? `${t('workday_title')} · ${formatHMS(live.workedSec)}` : t('workday_title')}
                    </Text>
                    <Text style={[styles.stripSub, {color: running ? 'rgba(226,236,255,0.78)' : m.colors.subText}]} numberOfLines={2}>
                        {paused
                            ? `${t('workday_status_paused')} · ${t('workday_break_time')} ${formatHM(live.breakSec)}`
                            : (running ? t('workday_since', {time: fmtClock(state.running.startedAt)}) : t('workday_strip_hint'))}
                    </Text>
                </View>
            </Pressable>
            {running && (
                <Pressable
                    onPress={paused ? resume : pause}
                    disabled={busy}
                    accessibilityRole="button"
                    accessibilityLabel={paused ? t('workday_resume') : t('workday_pause')}
                    style={({pressed}) => [styles.stripBtn, styles.stripBtnRound, styles.stripBtnPause, pressed && styles.pressed]}
                >
                    <Icon name={paused ? 'play' : 'pause'} size={18} color="#FFFFFF" />
                </Pressable>
            )}
            <Pressable
                onPress={running ? requestStop : () => start({})}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel={running ? t('workday_stop') : t('workday_start')}
                style={({pressed}) => [styles.stripBtn, running ? [styles.stripBtnRound, styles.stripBtnStop] : {backgroundColor: BRAND.base}, pressed && styles.pressed]}
            >
                <Icon name={running ? 'stop' : 'play'} size={running ? 18 : 16} color="#FFFFFF" />
                {!running && <Text style={styles.stripBtnText} numberOfLines={1}>{t('workday_start')}</Text>}
            </Pressable>
        </LinearGradient>
    );
}

const styles = StyleSheet.create({
    hero: {borderRadius: 24, borderWidth: 1.2, padding: 16, gap: 14, marginBottom: 16},
    heroTop: {flexDirection: 'row', alignItems: 'center', gap: 14},
    heroText: {flex: 1, gap: 2},
    lottieWrap: {width: 72, height: 72, borderRadius: 24, alignItems: 'center', justifyContent: 'center', overflow: 'hidden'},
    lottie: {width: 64, height: 64},
    kicker: {fontSize: 10.5, fontWeight: '900', letterSpacing: 1.1},
    heroTitle: {fontSize: 18, fontWeight: '900', letterSpacing: -0.3},
    heroSub: {fontSize: 12.5, fontWeight: '600', lineHeight: 17},
    heroClock: {fontSize: 26, fontWeight: '900', letterSpacing: -0.6, fontVariant: ['tabular-nums']},
    ringTime: {fontSize: 17, fontWeight: '900', fontVariant: ['tabular-nums'], maxWidth: 58},
    strip: {borderRadius: 22, borderWidth: 1, padding: 12, flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14},
    stripMain: {flex: 1, flexDirection: 'row', alignItems: 'center', gap: 12},
    stripTitle: {fontSize: 15, fontWeight: '900', fontVariant: ['tabular-nums']},
    stripSub: {fontSize: 12, fontWeight: '600'},
    stripBtn: {minHeight: 42, borderRadius: 999, paddingHorizontal: 14, flexDirection: 'row', alignItems: 'center', gap: 6},
    stripBtnStop: {backgroundColor: 'rgba(239,68,68,0.9)'},
    stripBtnPause: {backgroundColor: BRAND.base},
    stripBtnRound: {width: 42, paddingHorizontal: 0, justifyContent: 'center'},
    stripBtnText: {color: '#FFFFFF', fontSize: 13, fontWeight: '900'},
    pressed: {opacity: 0.75, transform: [{scale: 0.98}]},
});
